import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import NumberFormat from "react-number-format";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Grid,
  Button,
  Box,
  IconButton,
  Icon,
  Text,
  CircularProgress,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
} from "@chakra-ui/react";
import AddSection from "Components/AddSection";
import Swal from "sweetalert2";
import api from "services/api";
import MaterialTable from "material-table";
import { tableIcons } from "utils/tableIcons";
import { useEffect, useState } from "react";
import { MdModeEdit, MdDelete, MdHelpOutline } from "react-icons/md";
import { useAuth } from "hooks/useAuth";
import { sortDESC } from "utils/sort";
import { useTour } from "@reactour/tour";

interface Contatos {
  id: number;
  nm_contato: string;
  telefone_contato: string;
  email_contato: string;
  setor: any;
}

interface Setors {
  id: number;
  nm_setor: string;
}

const schema = Yup.object().shape({
  nm_contato: Yup.string().required("Preencha o nome do Contato"),
  telefone_contato: Yup.string()
    .required("Preencha o número de telefone")
    .matches(
      /(\(?\d{2}\)?\s)?(\d{5}?\d{3})/,
      "Informe um número de telefone válido",
    ),
  email_contato: Yup.string()
    .email("Preencha um e-mail válido")
    .required("Preencha o e-mail do contato"),
  setor: Yup.string().required("Preencha o setor"),
});

const defaultValues = {
  nm_contato: "",
  telefone_contato: "",
  email_contato: "",
  setor: "",
};

const contactsSteps = [
  {
    selector: '[id="addButton"]',
    content:
      "Aqui você pode cadastrar contatos, sendo necessário informar: nome, telefone, e-mail e setor ao qual o contato pertence.",
  },
  {
    selector:
      '[class="MuiFormControl-root MuiTextField-root MTableToolbar-searchField-10"]',
    content:
      "É possível efetuar buscas dos contatos já cadastrados, a busca será aplicada em todos os campos preenchidos na hora do cadastro.",
  },
  {
    selector: '[aria-label="Editar"]',
    content:
      "Caso haja alguma mudança nos contatos cadastros, é possível realizar as edições nesse botão.",
  },
  {
    selector: '[aria-label="Deletar"]',
    content:
      "Assim como é totalmente possível a exclusão dos contatos que não pertencem  mais a organização.",
  },
];

const ContatosPortal = () => {
  const { setIsOpen, setSteps } = useTour();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [contatos, setContatos] = useState<Contatos[]>([]);
  const [setors, setSetors] = useState<Setors[]>([]);
  const [currentEditing, setCurrentEditing] = useState<null | number>(null);
  const [inputEditingNm_contato, setInputEditingNm_contato] = useState("");
  const [inputEditingTelefone_contato, setInputEditingTelefone_contato] =
    useState<any>("");
  const [inputEditingEmail_contato, setInputEditingEmail_contato] =
    useState("");
  const [inputEditingSetor, setInputEditingSetor] = useState<any>();
  const [editLoading, setEditLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const { user } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Contatos>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: Contatos) => {
    const data = {
      nm_contato: values.nm_contato,
      telefone_contato: parseInt(
        values.telefone_contato
          .replace("(", "")
          .replace(")", "")
          .replace(" ", ""),
      ),
      email_contato: values.email_contato,
      setor: values.setor,
      operadora: user?.operadora && user?.operadora.id,
      entidade: user?.entidade && user?.entidade.id,
      fl_operadora_entidade:
        user?.operadora !== null && user?.entidade === null
          ? "O"
          : user?.operadora === null && user.entidade !== null && "E",
    };

    await api
      .post("/contatoes", data)
      .then((res) => {
        setContatos([...contatos, res.data].sort(sortDESC));
        reset();
        Swal.fire({
          position: "top",
          icon: "success",
          title: "Contato cadastrado com sucesso",
          showConfirmButton: false,
          timer: 3000,
        });
      })
      .catch((error) => {
        console.log("erro: ", error);
        Swal.fire({
          icon: "warning",
          title:
            "Algo deu errado! Entre em contato com o administrador do sistema",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      });
  };

  async function handleEditContato() {
    Swal.fire({
      title: "Tem certeza que deseja editar esse contato?",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setEditLoading(true);
        try {
          const response = await api.put(`/contatoes/${currentEditing}`, {
            nm_contato: inputEditingNm_contato,
            telefone_contato: parseInt(
              inputEditingTelefone_contato
                .replace("(", "")
                .replace(")", "")
                .replace(" ", ""),
            ),
            email_contato: inputEditingEmail_contato,
            setor: inputEditingSetor,
          });

          const updatedContatos = contatos.map((contato) => {
            if (contato.id === currentEditing) {
              return response.data;
            }
            return contato;
          });

          setContatos(updatedContatos);

          setCurrentEditing(null);
        } catch (err) {
          console.log(err);

          Swal.fire({
            icon: "error",
            title: "Erro ao editar",
            showConfirmButton: true,
            confirmButtonText: `Ok`,
          });
        }
      }
      setInputEditingNm_contato("");
      setInputEditingTelefone_contato("");
      setInputEditingEmail_contato("");
      setInputEditingSetor(null);
      setCurrentEditing(null);
      setEditLoading(false);
    });
  }

  async function handleDeleteContato(id: number) {
    Swal.fire({
      title: "Tem certeza que deseja excluir esse contato?",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await api
          .delete(`/contatoes/${id}`)
          .then(async (res) => {
            setContatos(
              contatos.filter((item) => {
                return item["id"] !== id;
              }),
            );
            Swal.fire("Contato deletado com sucesso!", "", "success");
          })
          .catch((error) => {
            console.log("erro: ", error);
            Swal.fire({
              icon: "warning",
              title:
                "Algo deu errado ao deletar o contato! Entre em contato com o administrador do sistema",
              showConfirmButton: true,
              confirmButtonText: `Ok`,
            });
          });
      }
    });
  }

  useEffect(() => {
    async function getContatos() {
      const operadoraId =
        user?.operadora && (user?.operadora.id || user?.operadora);
      const entidadeId =
        user?.entidade && (user?.entidade.id || user?.entidade);

      try {
        if (operadoraId) {
          const response = await api.get(`/contatoes?operadora=${operadoraId}`);
          setContatos(response.data.sort(sortDESC));
        } else if (entidadeId) {
          const response = await api.get(`/contatoes?entidade=${entidadeId}`);
          setContatos(response.data.sort(sortDESC));
        }
      } catch (err) {
        console.log(err);
        setPageLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao listar os contatos",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      }
    }

    async function getSetors() {
      try {
        const response = await api.get("/setors");
        setSetors(response.data);
        setPageLoading(false);
      } catch (err) {
        console.log(err);
        setPageLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao listar as operadoras",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      }
    }
    getContatos();
    getSetors();
    setSteps(contactsSteps);
  }, [user, setSteps]);

  if (pageLoading) {
    return (
      <Box
        as="section"
        display="flex"
        w="100%"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress isIndeterminate color="blue.200" />
      </Box>
    );
  }

  return (
    <>
      <Box
        as="section"
        display="flex"
        flexDir="column"
        w="100%"
        maxHeight="calc(100vh - 72px)"
        overflow="auto"
        pb="4"
      >
        <Flex alignItems="center">
          <Box id="add-contact">
            <AddSection titleButton="Cadastrar Contato" scrollBehavior="inside">
              <form
                onSubmit={handleSubmit(onSubmit)}
                onReset={(event) => {
                  event.preventDefault();
                  reset(defaultValues);
                }}
              >
                <Grid gap={6}>
                  <FormControl
                    isInvalid={!!errors?.nm_contato?.message}
                    errortext={errors?.nm_contato?.message}
                    isRequired
                  >
                    <FormLabel htmlFor="nm_contato">Nome</FormLabel>
                    <Input
                      id="nm_contato"
                      type="text"
                      {...register("nm_contato")}
                    />
                    <FormErrorMessage>
                      {errors?.nm_contato?.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    isInvalid={!!errors?.telefone_contato?.message}
                    errortext={errors?.telefone_contato?.message}
                    isRequired
                  >
                    <FormLabel htmlFor="telefone_contato">Telefone</FormLabel>
                    <Controller
                      name="telefone_contato"
                      control={control}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #########"
                          customInput={Input}
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors?.telefone_contato?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors?.email_contato?.message}
                    errortext={errors?.email_contato?.message}
                    isRequired
                  >
                    <FormLabel htmlFor="email_contato">E-mail</FormLabel>
                    <Input
                      id="email_contato"
                      type="email"
                      {...register("email_contato")}
                    />
                    <FormErrorMessage>
                      {errors?.email_contato?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors?.setor?.message}
                    errortext={errors?.setor?.message}
                    isRequired
                  >
                    <FormLabel htmlFor="setor">Setor</FormLabel>
                    <Select
                      placeholder="Selecione um setor"
                      id="setor"
                      type="text"
                      {...register("setor")}
                    >
                      {setors &&
                        setors.map((setor) => (
                          <option key={setor.id} value={setor.id}>
                            {setor.nm_setor}
                          </option>
                        ))}
                    </Select>
                    <FormErrorMessage>
                      {errors?.setor?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <Button
                    isLoading={isSubmitting}
                    type="submit"
                    colorScheme="blue"
                    disabled={
                      !!errors.nm_contato ||
                      !!errors.telefone_contato ||
                      !!errors.email_contato ||
                      !!errors.setor
                    }
                  >
                    Salvar
                  </Button>
                </Grid>
              </form>
            </AddSection>
          </Box>
          <Button
            colorScheme="teal"
            variant="link"
            onClick={() => setIsOpen(true)}
          >
            <Icon as={MdHelpOutline} mr="5px" />
            Ajuda
          </Button>
        </Flex>
        <Box as="form" paddingInline="4" margin="0 15px 15px">
          <MaterialTable
            icons={tableIcons}
            title="Contatos"
            data={contatos && contatos}
            localization={{
              body: {
                emptyDataSourceMessage: "Nenhum contato cadastrado.",
              },
              pagination: {
                labelDisplayedRows: "{from}-{to} de {count}",
                labelRowsSelect: "linhas",
                previousTooltip: "Página anterior",
                nextTooltip: "Próxima página",
                firstTooltip: "Primeira página",
                lastTooltip: "Última página",
              },
              toolbar: {
                searchPlaceholder: "Procurar",
              },
            }}
            options={{ emptyRowsWhenPaging: false, tableLayout: "auto" }}
            columns={[
              {
                title: "Nome",
                field: "nm_contato",
                cellStyle: {
                  width: "23%",
                },
                render: (element) => {
                  return <Text as="p">{element.nm_contato}</Text>;
                },
              },
              {
                title: "Telefone",
                field: "telefone_contato",
                cellStyle: {
                  width: "23%",
                },
                render: (element) => {
                  return (
                    <Text as="p">
                      {[
                        element?.telefone_contato?.slice(0, 0),
                        "(",
                        element?.telefone_contato?.slice(0, 2),
                        ") ",
                        element?.telefone_contato?.slice(2),
                      ].join("")}
                    </Text>
                  );
                },
              },
              {
                title: "E-mail",
                field: "email_contato",
                cellStyle: {
                  width: "23%",
                },
                render: (element) => {
                  return <Text as="p">{element.email_contato}</Text>;
                },
              },
              {
                title: "Setor",
                field: "setor",
                cellStyle: {
                  width: "23%",
                },
                render: (element) => {
                  return <Text>{element.setor.nm_setor}</Text>;
                },
              },
              {
                title: "Editar",
                cellStyle: {
                  width: "4%",
                },
                render: (element) => {
                  return (
                    <IconButton
                      aria-label="Editar"
                      icon={<Icon as={MdModeEdit} />}
                      fontSize="24"
                      variant="unstyled"
                      onClick={() => {
                        onOpen();
                        setCurrentEditing(element.id);
                        setInputEditingNm_contato(element.nm_contato);
                        setInputEditingTelefone_contato(
                          element.telefone_contato,
                        );
                        setInputEditingEmail_contato(element.email_contato);
                        setInputEditingSetor(element.setor && element.setor.id);
                      }}
                    />
                  );
                },
              },
              {
                title: "Excluir",
                cellStyle: {
                  width: "4%",
                },
                render: (element) => {
                  return (
                    <IconButton
                      aria-label="Deletar"
                      icon={<Icon as={MdDelete} />}
                      fontSize="24"
                      variant="unstyled"
                      onClick={() => {
                        handleDeleteContato(element.id);
                      }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    />
                  );
                },
              },
            ]}
          />
        </Box>
      </Box>
      {currentEditing && (
        <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
          <ModalOverlay backgroundColor="blackAlpha.500" />
          <ModalContent
            paddingBottom="4"
            marginLeft="15px"
            marginRight="15px"
            marginTop="80px"
          >
            <ModalHeader>Editar Contato</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Grid gap={6}>
                <FormControl
                  isInvalid={inputEditingNm_contato?.length < 1 ? true : false}
                  isRequired
                >
                  <FormLabel htmlFor="nome_edit">Nome</FormLabel>
                  <Input
                    id="nome_edit"
                    name="nome_edit"
                    type="text"
                    onChange={(e) => {
                      setInputEditingNm_contato(e.target.value);
                    }}
                    defaultValue={inputEditingNm_contato}
                  />
                  <FormErrorMessage>
                    Esse campo não pode ficar vazio
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={
                    inputEditingTelefone_contato &&
                    parseInt(
                      inputEditingTelefone_contato
                        .replace("(", "")
                        .replace(")", "")
                        .replace(" ", ""),
                    ).toString().length < 10
                      ? true
                      : false
                  }
                  isRequired
                >
                  <FormLabel htmlFor="telefone_edit">Telefone</FormLabel>
                  <NumberFormat
                    customInput={Input}
                    format="(##) #########"
                    defaultValue={inputEditingTelefone_contato}
                    onChange={(e) => {
                      setInputEditingTelefone_contato(e.target.value);
                    }}
                  />
                  <FormErrorMessage>
                    Informe um número de telefone válido
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isInvalid={
                    inputEditingEmail_contato?.length < 1 ? true : false
                  }
                  isRequired
                >
                  <FormLabel htmlFor="email_edit">E-mail</FormLabel>
                  <Input
                    id="email_edit"
                    name="email_edit"
                    type="text"
                    onChange={(e) => {
                      setInputEditingEmail_contato(e.target.value);
                    }}
                    defaultValue={inputEditingEmail_contato}
                  />
                  <FormErrorMessage>
                    Esse campo não pode ficar vazio
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isInvalid={inputEditingSetor?.length < 1 ? true : false}
                  isRequired
                >
                  <FormLabel htmlFor="setor_edit">Setor</FormLabel>
                  <Select
                    placeholder="Selecione um setor"
                    id="setor_edit"
                    name="setor_edit"
                    type="text"
                    onChange={(e) => {
                      setInputEditingSetor(e.target.value);
                    }}
                    value={inputEditingSetor}
                  >
                    {setors &&
                      setors.map((setor) => (
                        <option key={setor.id} value={setor.id}>
                          {setor.nm_setor}
                        </option>
                      ))}
                  </Select>
                  <FormErrorMessage>
                    Esse campo não pode ficar vazio
                  </FormErrorMessage>
                </FormControl>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gridColumnGap="10px"
                >
                  <Button
                    width="130px"
                    isLoading={editLoading}
                    onClick={handleEditContato}
                    colorScheme="blue"
                    disabled={
                      inputEditingNm_contato?.length < 1 ||
                      (inputEditingTelefone_contato &&
                        parseInt(
                          inputEditingTelefone_contato
                            .replace("(", "")
                            .replace(")", "")
                            .replace(" ", ""),
                        ).toString().length < 10) ||
                      inputEditingEmail_contato?.length < 1 ||
                      inputEditingSetor?.length < 1
                    }
                  >
                    Salvar
                  </Button>
                  <Button
                    width="130px"
                    colorScheme="facebook"
                    onClick={() => {
                      onClose();
                      setInputEditingNm_contato("");
                      setInputEditingTelefone_contato("");
                      setInputEditingEmail_contato("");
                      setInputEditingSetor("");
                      setCurrentEditing(null);
                      setEditLoading(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Grid>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ContatosPortal;
